import React from 'react';
import styled, { css } from 'styled-components';
import { QRCodeSVG } from 'qrcode.react';

import { Typography } from 'src/typography';
import { PayByBankLogo } from 'src/assets/pay-by-bank-logo';
import LoadingSpinner from 'src/assets/loader/spinner';
import { PhoneBankGraphic } from 'src/assets/phone-bank-graphic';

type QrCodeContentProps = {
  instoreCartUrl: string | null;
  loading: boolean;
  handlePayLater: () => void;
};

export function QrCodeContent({ instoreCartUrl, loading, handlePayLater }: QrCodeContentProps): JSX.Element {
  return (
    <>
      <Content>
        <ContentContainer>
          <ContentHeading size='large' tag='h1'>
            Pay on your phone, <br /> skip the ATM & card fees
          </ContentHeading>
          <PhoneBankGraphic />
        </ContentContainer>
        <QrCodeContainer>
          {instoreCartUrl && !loading ? (
            <QRCodeSVG data-testid='qr-code' value={instoreCartUrl} size={200} />
          ) : (
            <Loader data-testid='loading-indicator'>
              <LoadingSpinner size={100} borderColor='#f3f6f8' />
            </Loader>
          )}
          <Typography.Body size='small' tag='p'>
            Scan to pay
          </Typography.Body>
        </QrCodeContainer>
      </Content>

      <FooterContainer>
        <PayByBankContainer>
          <PayByBankIcon width='70' height='13' />
        </PayByBankContainer>
      </FooterContainer>
      <PayLaterButton data-testid='pay-later-button' onClick={handlePayLater}>
        No thanks, pay at the counter
      </PayLaterButton>
    </>
  );
}

const Content = styled.div`
  ${({ theme: { spaces, colors } }) => css`
    align-items: center;
    justify-content: space-between;
    display: flex;
    height: 296px;
    min-width: 640px;
    background: ${colors.white};
    border-radius: 30px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    margin-top: ${spaces[6]};
    padding: ${spaces[3]} ${spaces[8]} 0;
  `}
`;

const ContentContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  margin-bottom: -12px;
`;

const ContentHeading = styled(Typography.Heading)`
  color: ${({ theme }) => theme.colors.primaryBlack};
  font-size: 24px;
  font-weight: 700;
  line-height: 30px;
`;

const QrCodeContainer = styled.div`
  ${({ theme: { colors, spaces } }) => css`
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: ${spaces[1]};
    justify-content: center;
    margin: ${spaces[6]} 0;
    min-height: 200px;
    min-width: 200px;
    border: 2px solid ${colors.grey[90]};
    border-radius: 10px;
    padding: ${spaces[2]};
    color: ${colors.grey[50]};

    p {
      text-transform: uppercase;
    }
  `}
`;

const FooterContainer = styled.div`
  ${({ theme: { colors, spaces } }) => css`
    background-color: ${colors.green10};
    border-radius: 0 0 30px 30px;
    padding: ${spaces[4]} ${spaces[6]} ${spaces[4]} ${spaces[6]};
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-width: 640px;
    height: 46px;
    margin-bottom: ${spaces[6]};
    font-size: 13px;
    color: ${colors.grey[50]};
  `}
`;

const PayByBankContainer = styled.div`
  align-items: flex-start;
  display: flex;
  gap: ${({ theme }) => theme.spaces[1]};
  justify-content: center;
`;

const PayByBankIcon = styled(PayByBankLogo)`
  margin-top: 1.25px;
`;

const Loader = styled.div`
  align-items: center;
  border-radius: 100px;
  display: flex;
  height: 100px;
  justify-content: center;
  width: 100px;
`;

const PayLaterButton = styled.button`
  background: transparent;
  border: none;
  color: #3387e1;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
`;
